import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { LuArrowUpRight } from 'react-icons/lu';

import Reveal from './Reveal';
import PROJECTS from './Projects_data';
import { consumeHomeScroll, consumeHomeState, saveHomePosition } from './scrollMemory';

import './Projects.scss'

const INITIAL_COUNT = 8;
const ROW_COUNT = 4;
const NON_CLIENT_IDS = ['16', '8', '4', '3', '2', '1'];

const CATEGORY_OPTIONS = [
    { id: 'web', label: 'Web' },
    { id: 'mobile', label: 'Mobile' },
    { id: 'software', label: 'Software' },
    { id: 'design', label: 'Design' },
    { id: 'shopify', label: 'Shopify' },
    { id: 'wordpress', label: 'WordPress' },
    { id: 'lms', label: 'LMS' },
    { id: 'erp', label: 'Enterprise' },
    { id: 'iot', label: 'IoT' },
    { id: 'rnd', label: 'R&D' },
];

const Projects = () => {
    const [selected, setSelected] = useState(null);
    const [visibleCount, setVisibleCount] = useState(INITIAL_COUNT);

    useEffect(() => {
        const saved = consumeHomeState();
        const savedY = consumeHomeScroll();

        if (saved) {
            setSelected(saved.selected ?? null);
            setVisibleCount(saved.visibleCount ?? INITIAL_COUNT);
        }

        if (savedY == null) return;

        const restore = () => window.scrollTo(0, savedY);
        const frame = requestAnimationFrame(restore);
        const timer = setTimeout(restore, 50);
        const timerLate = setTimeout(restore, 250);

        return () => {
            cancelAnimationFrame(frame);
            clearTimeout(timer);
            clearTimeout(timerLate);
        };
    }, []);

    const availableCategories = useMemo(() => {
        const present = new Set(PROJECTS.flatMap(project => project.categories || []));
        return CATEGORY_OPTIONS.filter(option => present.has(option.id));
    }, []);

    const filteredProjects = useMemo(() => {
        if (!selected) return PROJECTS;
        return PROJECTS.filter(project =>
            (project.categories || []).includes(selected)
        );
    }, [selected]);

    const isExpandable = visibleCount < filteredProjects.length;
    const showToggle = filteredProjects.length > INITIAL_COUNT;

    const chooseCategory = (id) => {
        setSelected(id);
        setVisibleCount(INITIAL_COUNT);
    };

    const toggle = () => {
        setVisibleCount(isExpandable ? visibleCount + ROW_COUNT : INITIAL_COUNT);
    };

    const openProject = () => {
        saveHomePosition({ selected, visibleCount });
    };

    return (
        <div className='projects'>
            <div className='project-filters'>
                <button
                    type='button'
                    className={`filter-chip ${selected === null ? 'is-active' : ''}`}
                    onClick={() => chooseCategory(null)}
                >
                    All
                </button>
                {availableCategories.map(option => (
                    <button
                        key={option.id}
                        type='button'
                        className={`filter-chip ${selected === option.id ? 'is-active' : ''}`}
                        onClick={() => chooseCategory(option.id)}
                    >
                        {option.label}
                    </button>
                ))}
            </div>

            {filteredProjects.length === 0 ? (
                <p className='projects-empty'>No projects match the selected categories.</p>
            ) : (
                <div className='projects-grid'>
                    {filteredProjects.slice(0, visibleCount).map((project, index) => {
                        const isClientProject = !NON_CLIENT_IDS.includes(project.id);

                        return (
                            <Reveal
                                key={project.id}
                                as={Link}
                                className='project-card'
                                delay={(index % ROW_COUNT) * 70}
                                to={`/projects/${project.id}`}
                                onClick={openProject}
                            >
                                <div className='project-thumb'>
                                    <img src={project.images[0]} alt={project.title} loading='lazy' />
                                    <span className='project-open'><LuArrowUpRight /></span>
                                </div>

                                <div className='project-info'>
                                    <span className='project-type'>{project.type}</span>
                                    <h3>{project.title}</h3>

                                    {isClientProject && project.client && (
                                        <span className='project-client'>
                                            {project.countryFlag && (
                                                <img src={project.countryFlag} alt={project.country || ''} loading='lazy' />
                                            )}
                                            {project.client}
                                        </span>
                                    )}
                                </div>
                            </Reveal>
                        )
                    })}
                </div>
            )}

            {showToggle && (
                <div className='projects-more'>
                    <button className='btn btn--ghost' onClick={toggle}>
                        {isExpandable ? 'Load more projects' : 'Show less'}
                    </button>
                </div>
            )}
        </div>
    )
}

export default Projects
